import React, { useCallback, useEffect, useMemo, useState } from "react";
import { DayPicker } from "react-day-picker";
import { format, parse } from "date-fns";
import { es as esLocale } from "date-fns/locale";
import { Icon } from "../icons/Icon";
import Label from "../label/Label";
import "react-day-picker/dist/style.css";
import "./calendar.css";
import { cn } from "@/lib/styles.utils";
import { literals } from "@/constants/literals";
import { formatDateDDMmmYYYY } from "@/lib/dates";

type Props = {
  /** Fecha "date-only" en formato YYYY-MM-DD (recomendado) */
  value?: string | null;
  onChange?: (d: string | null) => void;
  disabled?: boolean;
  name: string;
  className?: string;
  label?: string;
  required?: boolean;
  size?: "large" | "medium";
  helpText?: string;
  warning?: boolean;
  warningMessage?: string;
  error?: boolean;
  errorMessage?: string;
  success?: boolean;
  successMessage?: string;
  readOnly?: boolean;
  noBottomSpace?: boolean;
  lettersFormat?: boolean;
};

const InputCalendar = ({
  value = null,
  onChange,
  disabled,
  required,
  name,
  className = "",
  label,
  size = "medium",
  helpText,
  warning,
  warningMessage,
  error,
  errorMessage,
  success,
  successMessage,
  readOnly,
  noBottomSpace,
  lettersFormat,
}: Props) => {
  const containerRef = React.useRef<HTMLDivElement>(null);
  const [showHelpText, setShowHelpText] = useState(false);
  const [open, setOpen] = useState(false);

  const canInteract = !disabled && !readOnly;

  const requiredAlert = useMemo(() => required && !value, [required, value]);

  const showOrHideHelpText = useCallback(() => {
    setShowHelpText((v) => !v);
  }, []);

  const helpClasses = useMemo(() => {
    const helpTextClass = "text-xs leading-5";
    if (error || requiredAlert) {
      return {
        icon: "text-ib-red-main",
        text: `text-ib-red-main ${helpTextClass}`,
      };
    }
    if (warning) {
      return {
        icon: "text-ib-yellow-warning-darkest",
        text: `text-ib-orange-warning-dark ${helpTextClass}`,
      };
    }
    if (success) {
      return {
        icon: "text-ib-green-success-dark",
        text: `text-ib-green-success-dark ${helpTextClass}`,
      };
    }
    return {
      icon: "text-ib-gray-dark-2",
      text: `text-ib-gray-dark-2 ${helpTextClass}`,
    };
  }, [error, warning, success, requiredAlert]);

  const infoMessage = useMemo(() => {
    if (requiredAlert) return literals.required;
    if (warning) return warningMessage || helpText || null;
    if (error) return errorMessage || helpText || null;
    if (success) return successMessage || helpText || null;
    if (helpText && (showHelpText || disabled || readOnly)) return helpText;
    return null;
  }, [
    requiredAlert,
    warning,
    error,
    success,
    helpText,
    showHelpText,
    disabled,
    readOnly,
    warningMessage,
    errorMessage,
    successMessage,
  ]);

  const borderClass = useMemo(() => {
    if (error || requiredAlert) return "border-ib-red-main";
    if (warning) return "border-ib-orange-warning-dark";
    if (success) return "border-ib-green-success-dark";
    return "border-gray-300";
  }, [error, requiredAlert, warning, success]);

  // ✅ Convertimos string YYYY-MM-DD -> Date (solo para DayPicker)
  const selectedDate = useMemo(() => {
    if (!value) return undefined;
    // parse con formato fijo para evitar que el constructor Date interprete en UTC
    const d = parse(value, "yyyy-MM-dd", new Date());
    return Number.isNaN(d.getTime()) ? undefined : d;
  }, [value]);

  // ✅ Texto mostrado en el input: DD/MM/YYYY
  const display = useMemo(() => {
    if (!value) return "";
    const d = parse(value, "yyyy-MM-dd", new Date());
    if (Number.isNaN(d.getTime())) return "";
    return !lettersFormat
      ? format(d, "dd/MM/yyyy", { locale: esLocale })
      : formatDateDDMmmYYYY(d);
  }, [value, lettersFormat]);

  const fieldClasses = useMemo(() => {
    return cn(
      "relative flex items-center w-full rounded-xs border",
      readOnly ? "bg-ib-gray-lightest" : "bg-ib-white",
      "px-3 py-2",
      canInteract
        ? "cursor-text hover:border-ib-red-lightest-2"
        : "cursor-not-allowed",
      borderClass,
      size === "large" ? "text-base" : "text-sm",
    );
  }, [readOnly, canInteract, borderClass, size]);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (!containerRef.current) return;
      if (!containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }

    if (open) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [open]);
  return (
    <div
      ref={containerRef}
      className={cn("relative w-full flex flex-col gap-1", className)}
    >
      {label && (
        <div className="flex items-center gap-2">
          <Label label={label} id={name} required={required} />
          {helpText ? (
            <button type="button" onClick={showOrHideHelpText}>
              <Icon name="info" className="size-3 text-ib-gray-dark-2" />
            </button>
          ) : null}
        </div>
      )}

      <div
        className={fieldClasses}
        onClick={() => {
          if (canInteract) setOpen(true);
        }}
      >
        <input
          id={name}
          name={name}
          disabled={disabled}
          readOnly
          value={display}
          placeholder="01-Ene-2026"
          className={cn(
            "w-full bg-transparent outline-none",
            // espacio para clear + calendar
            "pr-16",
            "placeholder-gray-400 read-only:text-ib-gray-darkest",
          )}
        />

        {value && canInteract && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onChange?.(null);
            }}
            className="absolute right-9 top-1/2 -translate-y-1/2 inline-flex items-center justify-center"
            aria-label="Limpiar fecha"
            title="Limpiar"
          >
            <Icon name="closeCircle" className="size-4 text-ib-red-main" />
          </button>
        )}

        <button
          type="button"
          disabled={disabled}
          onClick={(e) => {
            e.stopPropagation();
            if (canInteract) setOpen((v) => !v);
          }}
          className="absolute right-3 top-1/2 -translate-y-1/2 inline-flex items-center justify-center"
          aria-label="Abrir calendario"
          title="Abrir calendario"
        >
          <Icon
            name="calendar"
            className={cn(
              readOnly ? "text-ib-gray-disabled" : "text-ib-red-main",
            )}
          />
        </button>
      </div>

      {!noBottomSpace && infoMessage && (
        <div className={cn("flex items-center gap-2", helpClasses.icon)}>
          <Icon name="info" className="size-4" />
          <span className={helpClasses.text}>{infoMessage}</span>
        </div>
      )}

      {open && canInteract && (
        <div
          data-testid="calendar-to-click"
          className="absolute z-50 mt-2 w-max rounded-xs border bg-white p-2 shadow-lg"
        >
          <DayPicker
            mode="single"
            selected={selectedDate}
            onSelect={(d) => {
              if (!d) {
                onChange?.(null);
                return;
              }
              // ✅ Guardamos como string date-only
              onChange?.(format(d, "yyyy-MM-dd"));
              setOpen(false);
            }}
            locale={esLocale}
            weekStartsOn={1}
            className="rdp-red"
            navLayout="around"
            captionLayout="dropdown"
          />
        </div>
      )}
    </div>
  );
};

export default InputCalendar;
